import React, { useState, useMemo, useEffect } from 'react';
import DatePicker from '../../../../components/shared/DatePicker/DatePicker';
import { TabPanel, TabView } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import DataTable from '../../../../components/shared/DataTable/DataTable';
import { DashboardChart } from '../../components/DashboardChart/DashboardChart';
import {
  getDefaultDateRange,
  getPeriodLabel,
  generateTimeseriesLabels,
} from '../../../../helpers/dateHelpers';
import {
  getInvoiceViewSales,
  getInvoiceViewSalesCounts,
  getInvoiceViewPurchases,
  getInvoiceViewPurchasesCounts,
  getInvoiceViewThirdparty,
  getInvoiceViewInvoiceList,
  getInvoiceThirdPartyStats,
  getInvoiceViewThirdpartyCounts,
} from '../../../../services/apis';
import { useAuthentication } from '../../../../hooks/useAuthentication';

import './Dashboard.css';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import i18n from '../../../../config/i18n';
import { Button } from 'primereact/button';

const Dashboard = () => {
  const { t } = useTranslation();
  const { userInfo } = useAuthentication();
  const [activePeriod, setActivePeriod] = useState(1);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState(0);
  const [updatedAtDate, setUpdatedAtDate] = useState(new Date());

  // Helper function to normalize dates based on period type
  const getDateKey = (date, periodType) => {
    const m = moment(date);
    switch (periodType) {
      case 'daily':
        return m.format('YYYY-MM-DD');
      case 'weekly':
        // Start of week (Monday)
        return m.startOf('week').format('YYYY-MM-DD');
      case 'monthly':
        // Start of month
        return m.format('YYYY-MM');
      case 'quarterly':
        // Start of quarter
        return `${m.year()}-Q${m.quarter()}`;
      default:
        return m.format('YYYY-MM-DD');
    }
  };

  // Set default date range (last 3 months)
  const defaultDates = getDefaultDateRange();

  const [filterDateFrom, setFilterDateFrom] = useState(
    moment(defaultDates.from).format('DD/MM/YYYY'),
  );
  const [filterDateTo, setFilterDateTo] = useState(
    moment(defaultDates.to).format('DD/MM/YYYY'),
  );
  const [salesData, setSalesData] = useState([]);
  const [salesCountsData, setSalesCountsData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [purchaseCountsData, setPurchaseCountsData] = useState([]);
  const [thirdPartyData, setThirdPartyData] = useState([]);
  const [thirdPartyStats, setThirdPartyStats] = useState(null);
  const [thirdPartyCountsData, setThirdPartyCountsData] = useState([]);
  const [latestInvoices, setLatestInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesDate, setInvoicesDate] = useState(new Date());

  const [dateFromValue, setDateFromValue] = useState(defaultDates.from);
  const [dateToValue, setDateToValue] = useState(defaultDates.to);

  const getPeriodType = () => getPeriodLabel(activePeriod);

  // Fetch sales data from API
  useEffect(() => {
    const fetchSalesData = async () => {
      if (!dateFromValue || !dateToValue) {
        return;
      }

      setLoading(true);
      try {
        const [
          salesResponse,
          countsResponse,
          purchaseResponse,
          purchaseCountsResponse,
          thirdPartyResponse,
          thirdPartyStatsResponse,
          thirdPartyCountsResponse,
        ] = await Promise.all([
          getInvoiceViewSales({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceViewSalesCounts({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceViewPurchases({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceViewPurchasesCounts({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceViewThirdparty({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceThirdPartyStats({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
          getInvoiceViewThirdpartyCounts({
            startDate: moment(dateFromValue).format('YYYY-MM-DD'),
            endDate: moment(dateToValue).format('YYYY-MM-DD'),
          }),
        ]);
        setSalesData(salesResponse?.data || []);
        setSalesCountsData(countsResponse?.data || []);
        setPurchaseData(purchaseResponse?.data || []);
        setPurchaseCountsData(purchaseCountsResponse?.data || []);
        setThirdPartyData(thirdPartyResponse?.data || []);
        setThirdPartyStats(thirdPartyStatsResponse?.data || null);
        setThirdPartyCountsData(thirdPartyCountsResponse?.data || []);
        setUpdatedAtDate(new Date());
      } catch (error) {
        console.error('Error fetching sales data:', error);
        setSalesData([]);
        setSalesCountsData([]);
        setPurchaseData([]);
        setPurchaseCountsData([]);
        setThirdPartyData([]);
        setThirdPartyStats(null);
        setThirdPartyCountsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [dateFromValue, dateToValue]);

  useEffect(() => {
    if (filterDateFrom) {
      setDateFromValue(moment(filterDateFrom, 'DD/MM/YYYY'));
    }
    if (filterDateTo) {
      setDateToValue(moment(filterDateTo, 'DD/MM/YYYY'));
    }
  }, [filterDateFrom, filterDateTo]);

  const fetchLatestInvoices = async () => {
    if (!userInfo?.tax_number) {
      return;
    }

    const viewMap = ['SALES', 'PURCHASES', 'THIRD_PARTY'];
    const view = viewMap[activeInvoiceTab];

    setInvoicesLoading(true);
    try {
      const response = await getInvoiceViewInvoiceList({
        view,
        vatNumber: userInfo.tax_number,
      });
      setLatestInvoices(response?.data?.slice(0, 10) || []);
      setInvoicesDate(new Date());
    } catch (error) {
      console.error('Error fetching latest invoices:', error);
      setLatestInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Fetch latest invoices based on active tab
  useEffect(() => {
    fetchLatestInvoices();
  }, [activeInvoiceTab, userInfo?.tax_number]);

  // Generate labels and data based on date range
  const { labels: timeseriesLabels } = useMemo(() => {
    const generatedLabels = generateTimeseriesLabels(
      dateFromValue,
      dateToValue,
      getPeriodType(),
    );
    return { labels: generatedLabels };
  }, [dateFromValue, dateToValue, activePeriod]);

  // Calculate sales totals from API data
  const salesTotals = useMemo(() => {
    if (!salesData || salesData.length === 0) {
      return {
        totalInvoices: 0,
        totalGross: 0,
        totalVat: 0,
        totalNet: 0,
      };
    }

    return salesData.reduce(
      (acc, item) => ({
        totalInvoices: acc.totalInvoices + (item.numberOfInvoices || 0),
        totalGross: acc.totalGross + (item.grossTotalAmount || 0),
        totalVat: acc.totalVat + (item.vatAmount || 0),
        totalNet: acc.totalNet + (item.netAmount || 0),
      }),
      { totalInvoices: 0, totalGross: 0, totalVat: 0, totalNet: 0 },
    );
  }, [salesData, dateFromValue, dateToValue, activePeriod]);

  // Calculate sales invoice status totals from API data
  const salesStatusTotals = useMemo(() => {
    if (!salesCountsData || salesCountsData.length === 0) {
      return {
        acceptedVat: 0,
        warningVat: 0,
        rejectedVat: 0,
        acceptedSimplified: 0,
        warningSimplified: 0,
        rejectedSimplified: 0,
      };
    }

    return salesCountsData.reduce(
      (acc, item) => ({
        acceptedVat: acc.acceptedVat + (item.acceptedVat || 0),
        warningVat: acc.warningVat + (item.warningVat || 0),
        rejectedVat: acc.rejectedVat + (item.rejectedVat || 0),
        acceptedSimplified:
          acc.acceptedSimplified + (item.acceptedSimplified || 0),
        warningSimplified:
          acc.warningSimplified + (item.warningSimplified || 0),
        rejectedSimplified:
          acc.rejectedSimplified + (item.rejectedSimplified || 0),
      }),
      {
        acceptedVat: 0,
        warningVat: 0,
        rejectedVat: 0,
        acceptedSimplified: 0,
        warningSimplified: 0,
        rejectedSimplified: 0,
      },
    );
  }, [salesCountsData]);

  // Calculate purchase totals from API data
  const purchaseTotals = useMemo(() => {
    if (!purchaseData || purchaseData.length === 0) {
      return {
        totalInvoices: 0,
        totalGross: 0,
        totalVat: 0,
        totalNet: 0,
      };
    }

    return purchaseData.reduce(
      (acc, item) => ({
        totalInvoices: acc.totalInvoices + (item.numberOfInvoices || 0),
        totalGross: acc.totalGross + (item.grossTotalAmount || 0),
        totalVat: acc.totalVat + (item.vatAmount || 0),
        totalNet: acc.totalNet + (item.netAmount || 0),
      }),
      { totalInvoices: 0, totalGross: 0, totalVat: 0, totalNet: 0 },
    );
  }, [purchaseData]);

  // Calculate third party totals from API data
  const thirdPartyTotals = useMemo(() => {
    if (thirdPartyStats) {
      return {
        activeAgreements: thirdPartyStats.activeThirdParty || 0,
        totalInvoices: thirdPartyStats.numberOfInvoicesIssued || 0,
      };
    }

    return {
      activeAgreements: 0,
      totalInvoices: 0,
    };
  }, [thirdPartyStats]);

  // Calculate third party invoice status totals from API data
  const thirdPartyStatusTotals = useMemo(() => {
    if (!thirdPartyCountsData || thirdPartyCountsData.length === 0) {
      return {
        acceptedVat: 0,
        warningVat: 0,
        rejectedVat: 0,
        acceptedSimplified: 0,
        warningSimplified: 0,
        rejectedSimplified: 0,
      };
    }

    return thirdPartyCountsData.reduce(
      (acc, item) => ({
        acceptedVat: acc.acceptedVat + (item.acceptedVat || 0),
        warningVat: acc.warningVat + (item.warningVat || 0),
        rejectedVat: acc.rejectedVat + (item.rejectedVat || 0),
        acceptedSimplified:
          acc.acceptedSimplified + (item.acceptedSimplified || 0),
        warningSimplified:
          acc.warningSimplified + (item.warningSimplified || 0),
        rejectedSimplified:
          acc.rejectedSimplified + (item.rejectedSimplified || 0),
      }),
      {
        acceptedVat: 0,
        warningVat: 0,
        rejectedVat: 0,
        acceptedSimplified: 0,
        warningSimplified: 0,
        rejectedSimplified: 0,
      },
    );
  }, [thirdPartyCountsData]);

  // Transform sales data for chart
  const chartSalesData = useMemo(() => {
    if (!salesData || salesData.length === 0 || !timeseriesLabels) {
      return { salesNetAmount: [], salesVat: [] };
    }

    const periodType = getPeriodType();

    // Create a map to aggregate data by date using normalized keys
    const dataByDate = salesData.reduce((acc, item) => {
      const dateKey = getDateKey(item.date, periodType);
      if (!acc[dateKey]) {
        acc[dateKey] = { netAmount: 0, vatAmount: 0 };
      }
      acc[dateKey].netAmount += item.netAmount || 0;
      acc[dateKey].vatAmount += item.vatAmount || 0;
      return acc;
    }, {});

    // Map to timeseries labels (labels are Date objects)
    const salesNetAmount = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.netAmount || 0;
    });

    const salesVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.vatAmount || 0;
    });

    return { salesNetAmount, salesVat };
  }, [salesData, timeseriesLabels, filterDateFrom, filterDateTo, activePeriod]);

  // Transform purchase data for chart
  const chartPurchaseData = useMemo(() => {
    if (!purchaseData || purchaseData.length === 0 || !timeseriesLabels) {
      return { purchaseNetAmount: [], purchaseVat: [] };
    }

    const periodType = getPeriodType();

    // Create a map to aggregate data by date using normalized keys
    const dataByDate = purchaseData.reduce((acc, item) => {
      const dateKey = getDateKey(item.date, periodType);
      if (!acc[dateKey]) {
        acc[dateKey] = { netAmount: 0, vatAmount: 0 };
      }
      acc[dateKey].netAmount += item.netAmount || 0;
      acc[dateKey].vatAmount += item.vatAmount || 0;
      return acc;
    }, {});

    // Map to timeseries labels (labels are Date objects)
    const purchaseNetAmount = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.netAmount || 0;
    });

    const purchaseVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.vatAmount || 0;
    });

    return { purchaseNetAmount, purchaseVat };
  }, [purchaseData, timeseriesLabels, activePeriod]);

  // Transform sales counts data for charts
  const chartSalesCountsData = useMemo(() => {
    if (!salesCountsData || salesCountsData.length === 0 || !timeseriesLabels) {
      return {
        acceptedVat: [],
        warningVat: [],
        rejectedVat: [],
        acceptedSimplified: [],
        warningSimplified: [],
        rejectedSimplified: [],
      };
    }

    const periodType = getPeriodType();

    // Create a map to aggregate data by date using normalized keys
    const dataByDate = salesCountsData.reduce((acc, item) => {
      const dateKey = getDateKey(item.date, periodType);
      if (!acc[dateKey]) {
        acc[dateKey] = {
          acceptedVat: 0,
          warningVat: 0,
          rejectedVat: 0,
          acceptedSimplified: 0,
          warningSimplified: 0,
          rejectedSimplified: 0,
        };
      }
      acc[dateKey].acceptedVat += item.acceptedVat || 0;
      acc[dateKey].warningVat += item.warningVat || 0;
      acc[dateKey].rejectedVat += item.rejectedVat || 0;
      acc[dateKey].acceptedSimplified += item.acceptedSimplified || 0;
      acc[dateKey].warningSimplified += item.warningSimplified || 0;
      acc[dateKey].rejectedSimplified += item.rejectedSimplified || 0;
      return acc;
    }, {});

    // Map to timeseries labels
    const acceptedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedVat || 0;
    });

    const warningVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningVat || 0;
    });

    const rejectedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedVat || 0;
    });

    const acceptedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedSimplified || 0;
    });

    const warningSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningSimplified || 0;
    });

    const rejectedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedSimplified || 0;
    });

    return {
      acceptedVat,
      warningVat,
      rejectedVat,
      acceptedSimplified,
      warningSimplified,
      rejectedSimplified,
    };
  }, [salesCountsData, timeseriesLabels, activePeriod]);

  // Transform purchase counts data for charts
  const chartPurchaseCountsData = useMemo(() => {
    if (
      !purchaseCountsData ||
      purchaseCountsData.length === 0 ||
      !timeseriesLabels
    ) {
      return {
        acceptedVat: [],
        warningVat: [],
        rejectedVat: [],
        acceptedSimplified: [],
        warningSimplified: [],
        rejectedSimplified: [],
      };
    }

    const periodType = getPeriodType();

    // Create a map to aggregate data by date using normalized keys
    const dataByDate = purchaseCountsData.reduce((acc, item) => {
      const dateKey = getDateKey(item.date, periodType);
      if (!acc[dateKey]) {
        acc[dateKey] = {
          acceptedVat: 0,
          warningVat: 0,
          rejectedVat: 0,
          acceptedSimplified: 0,
          warningSimplified: 0,
          rejectedSimplified: 0,
        };
      }
      acc[dateKey].acceptedVat += item.acceptedVat || 0;
      acc[dateKey].warningVat += item.warningVat || 0;
      acc[dateKey].rejectedVat += item.rejectedVat || 0;
      acc[dateKey].acceptedSimplified += item.acceptedSimplified || 0;
      acc[dateKey].warningSimplified += item.warningSimplified || 0;
      acc[dateKey].rejectedSimplified += item.rejectedSimplified || 0;
      return acc;
    }, {});

    // Map to timeseries labels
    const acceptedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedVat || 0;
    });

    const warningVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningVat || 0;
    });

    const rejectedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedVat || 0;
    });

    const acceptedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedSimplified || 0;
    });

    const warningSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningSimplified || 0;
    });

    const rejectedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedSimplified || 0;
    });

    return {
      acceptedVat,
      warningVat,
      rejectedVat,
      acceptedSimplified,
      warningSimplified,
      rejectedSimplified,
    };
  }, [purchaseCountsData, timeseriesLabels, activePeriod]);

  // Transform third party counts data for charts
  const chartThirdPartyCountsData = useMemo(() => {
    if (
      !thirdPartyCountsData ||
      thirdPartyCountsData.length === 0 ||
      !timeseriesLabels
    ) {
      return {
        acceptedVat: [],
        warningVat: [],
        rejectedVat: [],
        acceptedSimplified: [],
        warningSimplified: [],
        rejectedSimplified: [],
      };
    }

    const periodType = getPeriodType();

    // Create a map to aggregate data by date using normalized keys
    const dataByDate = thirdPartyCountsData.reduce((acc, item) => {
      const dateKey = getDateKey(item.date, periodType);
      if (!acc[dateKey]) {
        acc[dateKey] = {
          acceptedVat: 0,
          warningVat: 0,
          rejectedVat: 0,
          acceptedSimplified: 0,
          warningSimplified: 0,
          rejectedSimplified: 0,
        };
      }
      acc[dateKey].acceptedVat += item.acceptedVat || 0;
      acc[dateKey].warningVat += item.warningVat || 0;
      acc[dateKey].rejectedVat += item.rejectedVat || 0;
      acc[dateKey].acceptedSimplified += item.acceptedSimplified || 0;
      acc[dateKey].warningSimplified += item.warningSimplified || 0;
      acc[dateKey].rejectedSimplified += item.rejectedSimplified || 0;
      return acc;
    }, {});

    // Map to timeseries labels
    const acceptedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedVat || 0;
    });

    const warningVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningVat || 0;
    });

    const rejectedVat = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedVat || 0;
    });

    const acceptedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.acceptedSimplified || 0;
    });

    const warningSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.warningSimplified || 0;
    });

    const rejectedSimplified = timeseriesLabels.map((label) => {
      const dateKey = getDateKey(label, periodType);
      return dataByDate[dateKey]?.rejectedSimplified || 0;
    });

    return {
      acceptedVat,
      warningVat,
      rejectedVat,
      acceptedSimplified,
      warningSimplified,
      rejectedSimplified,
    };
  }, [thirdPartyCountsData, timeseriesLabels, activePeriod]);

  // Generate sample data matching the number of labels
  const chartData = useMemo(() => {
    const periodType = getPeriodType();

    return {
      salesNetAmount: chartSalesData.salesNetAmount,
      salesVat: chartSalesData.salesVat,
      salesVatInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = salesCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum +
            (item.acceptedVat || 0) +
            (item.warningVat || 0) +
            (item.rejectedVat || 0),
          0,
        );
      }),
      salesSimplifiedVat: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = salesCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum +
            (item.acceptedSimplified || 0) +
            (item.warningSimplified || 0) +
            (item.rejectedSimplified || 0),
          0,
        );
      }),
      purchaseNetAmount: chartPurchaseData.purchaseNetAmount,
      purchaseVat: chartPurchaseData.purchaseVat,
      purchaseVatInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = purchaseCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum +
            (item.acceptedVat || 0) +
            (item.warningVat || 0) +
            (item.rejectedVat || 0),
          0,
        );
      }),
      purchaseSimplifiedVat: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = purchaseCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum +
            (item.acceptedSimplified || 0) +
            (item.warningSimplified || 0) +
            (item.rejectedSimplified || 0),
          0,
        );
      }),
      outputVat: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const dataForDate = salesData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return dataForDate.reduce(
          (sum, item) => sum + (item.vatAmount || 0),
          0,
        );
      }),
      inputVat: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const dataForDate = purchaseData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return dataForDate.reduce(
          (sum, item) => sum + (item.vatAmount || 0),
          0,
        );
      }),
      netVat: timeseriesLabels.map((label, index) => {
        const dateKey = getDateKey(label, periodType);
        const salesDataForDate = salesData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        const purchaseDataForDate = purchaseData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        const outputVat = salesDataForDate.reduce(
          (sum, item) => sum + (item.vatAmount || 0),
          0,
        );
        const inputVat = purchaseDataForDate.reduce(
          (sum, item) => sum + (item.vatAmount || 0),
          0,
        );
        return outputVat - inputVat;
      }),
      thirdPartyInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const dataForDate = thirdPartyData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return dataForDate.reduce(
          (sum, item) => sum + (item.vatInvoices || 0),
          0,
        );
      }),
      thirdPartySimplified: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const dataForDate = thirdPartyData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return dataForDate.reduce(
          (sum, item) => sum + (item.simplifiedInvoices || 0),
          0,
        );
      }),
      salesAcceptedInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = salesCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.acceptedVat || 0) + (item.acceptedSimplified || 0),
          0,
        );
      }),
      salesAcceptedWithWarnings: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = salesCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.warningVat || 0) + (item.warningSimplified || 0),
          0,
        );
      }),
      salesRejectedInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = salesCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.rejectedVat || 0) + (item.rejectedSimplified || 0),
          0,
        );
      }),
      thirdPartyAcceptedInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = thirdPartyCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.acceptedVat || 0) + (item.acceptedSimplified || 0),
          0,
        );
      }),
      thirdPartyAcceptedWithWarnings: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = thirdPartyCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.warningVat || 0) + (item.warningSimplified || 0),
          0,
        );
      }),
      thirdPartyRejectedInvoices: timeseriesLabels.map((label) => {
        const dateKey = getDateKey(label, periodType);
        const countsForDate = thirdPartyCountsData.filter(
          (item) => getDateKey(item.date, periodType) === dateKey,
        );
        return countsForDate.reduce(
          (sum, item) =>
            sum + (item.rejectedVat || 0) + (item.rejectedSimplified || 0),
          0,
        );
      }),
    };
  }, [
    timeseriesLabels,
    chartSalesData,
    salesCountsData,
    chartSalesCountsData,
    chartPurchaseData,
    purchaseCountsData,
    chartPurchaseCountsData,
    thirdPartyData,
    thirdPartyCountsData,
    chartThirdPartyCountsData,
    activePeriod,
  ]);

  // Calculate third party data by agreement
  const thirdPartyByAgreement = useMemo(() => {
    if (!thirdPartyData || thirdPartyData.length === 0) {
      return {
        labels: [],
        data: [],
      };
    }

    // Aggregate by agreement
    const aggregated = thirdPartyData.reduce((acc, item) => {
      if (!acc[item.agreement]) {
        acc[item.agreement] = 0;
      }
      acc[item.agreement] +=
        (item.vatInvoices || 0) + (item.simplifiedInvoices || 0);
      return acc;
    }, {});

    // Sort by invoice count and get top agreements
    const sorted = Object.entries(aggregated)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return {
      labels: sorted.map(([agreement]) => agreement),
      data: sorted.map(([, count]) => count),
    };
  }, [thirdPartyData]);

  const relationShipLabels =
    thirdPartyByAgreement.labels.length > 0 ? thirdPartyByAgreement.labels : [];

  const relationShipData =
    thirdPartyByAgreement.data.length > 0 ? thirdPartyByAgreement.data : [];

  return (
    <div className="my-invoices-dashboard">
      <h2>{t('invoice_view_page.dashboard.title')}</h2>

      <div className="filters">
        <p className="updated-at flex-grow-1">
          {t('invoice_view_page.dashboard.stats_desc', {
            date: moment(updatedAtDate)
              .locale(i18n.language)
              .format('DD MMM YYYY, h:mm A'),
          })}
        </p>

        <div className="date-filter">
          <div className="label">{t('common.from')}</div>
          <DatePicker
            onChange={(e) => setFilterDateFrom(e)}
            value={filterDateFrom}
            showIcon
          />
          <div className="label">{t('common.to')}</div>
          <DatePicker
            onChange={(e) => setFilterDateTo(e)}
            value={filterDateTo}
            showIcon
          />
        </div>
        <div className="period-filter">
          <div className="label">
            {t('invoice_view_page.dashboard.view-by')}:
          </div>
          <TabView
            activeIndex={activePeriod}
            onTabChange={(e) => setActivePeriod(e.index)}
          >
            <TabPanel header={t('invoice_view_page.dashboard.daily')} />
            <TabPanel header={t('invoice_view_page.dashboard.weekly')} />
            <TabPanel header={t('invoice_view_page.dashboard.monthly')} />
            <TabPanel header={t('invoice_view_page.dashboard.quarterly')} />
          </TabView>
        </div>
      </div>

      <div className="sales-details">
        <h3>{t('invoice_view_page.dashboard.sales-details')}</h3>
        <div className="row gap-4 mb-4">
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.number-of-e-invoices-issued')}
            </div>
            <div className="number">
              {loading ? '...' : salesTotals.totalInvoices.toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.gross-total-including-vat')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : `BHD ${salesTotals.totalGross.toLocaleString()}`}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.vat-amount')}
            </div>
            <div className="number">
              {loading ? '...' : `BHD ${salesTotals.totalVat.toLocaleString()}`}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.net-amount-excluding-vat')}
            </div>
            <div className="number">
              {loading ? '...' : `BHD ${salesTotals.totalNet.toLocaleString()}`}
            </div>
          </div>
        </div>

        <div className="row gap-4 justify-content-between">
          <div className="col chart-container">
            <DashboardChart
              title={t('invoice_view_page.dashboard.total-sales-bhd')}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="sales-amount"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.net-amount-excl-vat'),
                  backgroundColor: '#1E2D30',
                  data: chartData.salesNetAmount,
                },
                {
                  label: t('invoice_view_page.dashboard.vat-amount'),
                  backgroundColor: '#47696F',
                  data: chartData.salesVat,
                },
              ]}
            />
          </div>
          <div className="col chart-container">
            <DashboardChart
              title={t(
                'invoice_view_page.dashboard.number-of-e-invoices-issued',
              )}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="sales-count"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.vat-e-invoices'),
                  backgroundColor: '#1E2D30',
                  data: chartData.salesVatInvoices,
                },
                {
                  label: t(
                    'invoice_view_page.dashboard.simplified-vat-e-invoices',
                  ),
                  backgroundColor: '#A6BBBF',
                  data: chartData.salesSimplifiedVat,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="purchase-details">
        <h3>{t('invoice_view_page.dashboard.purchase-details')}</h3>
        <div className="row gap-4 mb-4">
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.number-of-e-invoices-received')}
            </div>
            <div className="number">
              {loading ? '...' : purchaseTotals.totalInvoices.toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.gross-total-including-vat')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : `BHD ${purchaseTotals.totalGross.toLocaleString()}`}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.vat-amount')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : `BHD ${purchaseTotals.totalVat.toLocaleString()}`}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.net-amount-excluding-vat')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : `BHD ${purchaseTotals.totalNet.toLocaleString()}`}
            </div>
          </div>
        </div>

        <div className="row gap-4 justify-content-between">
          <div className="col chart-container">
            <DashboardChart
              title={t('invoice_view_page.dashboard.total-purchases-bhd')}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="purchase-amount"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.net-amount-excl-vat'),
                  backgroundColor: '#1E2D30',
                  data: chartData.purchaseNetAmount,
                },
                {
                  label: t('invoice_view_page.dashboard.vat-amount'),
                  backgroundColor: '#47696F',
                  data: chartData.purchaseVat,
                },
              ]}
            />
          </div>
          <div className="col chart-container">
            <DashboardChart
              title={t(
                'invoice_view_page.dashboard.number-of-e-invoices-received',
              )}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="purchase-count"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.vat-e-invoices'),
                  backgroundColor: '#1E2D30',
                  data: chartData.purchaseVatInvoices,
                },
                {
                  label: t(
                    'invoice_view_page.dashboard.simplified-vat-e-invoices',
                  ),
                  backgroundColor: '#A6BBBF',
                  data: chartData.purchaseSimplifiedVat,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="vat-details">
        <h3>{t('invoice_view_page.dashboard.vat-details')}</h3>

        <div className="row gap-4 justify-content-between">
          <div className="col chart-container">
            <TabView>
              <TabPanel header={t('invoice_view_page.dashboard.output-vat')}>
                <DashboardChart
                  isTimeseries={true}
                  dateFrom={dateFromValue}
                  dateTo={dateToValue}
                  periodType={getPeriodType()}
                  showLegend={false}
                  chartType="vat-details"
                  title={t('invoice_view_page.dashboard.output-vat-in-bhd')}
                  vatData={{
                    outputVat: chartData.outputVat,
                    inputVat: chartData.inputVat,
                    netVat: chartData.netVat,
                  }}
                  dataset={[
                    {
                      label: '',
                      backgroundColor: '#1E2D30',
                      data: chartData.outputVat,
                    },
                  ]}
                />
              </TabPanel>
              <TabPanel header={t('invoice_view_page.dashboard.input-vat')}>
                <DashboardChart
                  isTimeseries={true}
                  dateFrom={dateFromValue}
                  dateTo={dateToValue}
                  periodType={getPeriodType()}
                  showLegend={false}
                  title={t('invoice_view_page.dashboard.input-vat')}
                  chartType="vat-details"
                  vatData={{
                    outputVat: chartData.outputVat,
                    inputVat: chartData.inputVat,
                    netVat: chartData.netVat,
                  }}
                  dataset={[
                    {
                      label: '',
                      backgroundColor: '#1E2D30',
                      data: chartData.inputVat,
                    },
                  ]}
                />
              </TabPanel>
              <TabPanel header={t('invoice_view_page.dashboard.net-vat')}>
                <DashboardChart
                  isTimeseries={true}
                  dateFrom={dateFromValue}
                  dateTo={dateToValue}
                  periodType={getPeriodType()}
                  title={t('invoice_view_page.dashboard.net-vat')}
                  showLegend={false}
                  chartType="vat-details"
                  vatData={{
                    outputVat: chartData.outputVat,
                    inputVat: chartData.inputVat,
                    netVat: chartData.netVat,
                  }}
                  dataset={[
                    {
                      label: '',
                      backgroundColor: '#1E2D30',
                      data: chartData.netVat,
                    },
                  ]}
                />
              </TabPanel>
            </TabView>
          </div>
          <div className="col" />
        </div>
      </div>

      <div className="third-party-transactions">
        <h3>{t('invoice_view_page.dashboard.third-party-transactions')}</h3>
        <div className="row gap-4 mb-4">
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.active-third-party-relationship')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : thirdPartyTotals.activeAgreements.toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.number-of-e-invoices-issued')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : thirdPartyTotals.totalInvoices.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="row gap-4 justify-content-between">
          <div className="col chart-container">
            <DashboardChart
              title={t(
                'invoice_view_page.dashboard.total-number-of-e-invoices-issued',
              )}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="third-party-count"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.vat-e-invoices'),
                  backgroundColor: '#1E2D30',
                  data: chartData.thirdPartyInvoices,
                },
                {
                  label: t(
                    'invoice_view_page.dashboard.simplified-vat-e-invoices',
                  ),
                  backgroundColor: '#A6BBBF',
                  data: chartData.thirdPartySimplified,
                },
              ]}
            />
          </div>
          <div className="col chart-container">
            <DashboardChart
              title={t(
                'invoice_view_page.dashboard.number-of-e-invoices-issued-per-relationship',
              )}
              labels={relationShipLabels}
              indexAxis={'y'}
              borderRadius={50}
              chartType="third-party-relationship"
              dataset={[
                {
                  label: t(
                    'invoice_view_page.dashboard.total-number-of-e-invoices-issued',
                  ),
                  backgroundColor: '#1E2D30',
                  data: relationShipData,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="einvoice-status">
        <h3>{t('invoice_view_page.dashboard.e-invoice-status')}</h3>
        <div className="row gap-4 mb-4">
          <div className="col">
            <h4>{t('invoice_view_page.dashboard.sales-e-invoices')}</h4>
          </div>
          <div className="col">
            <h4>{t('invoice_view_page.dashboard.third-party-e-invoices')}</h4>
          </div>
        </div>
        <div className="row gap-4 mb-4">
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.accepted')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    salesStatusTotals.acceptedVat +
                    salesStatusTotals.acceptedSimplified
                  ).toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.accepted-with-warnings')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    salesStatusTotals.warningVat +
                    salesStatusTotals.warningSimplified
                  ).toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.rejected')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    salesStatusTotals.rejectedVat +
                    salesStatusTotals.rejectedSimplified
                  ).toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.accepted')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    thirdPartyStatusTotals.acceptedVat +
                    thirdPartyStatusTotals.acceptedSimplified
                  ).toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.accepted-with-warnings')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    thirdPartyStatusTotals.warningVat +
                    thirdPartyStatusTotals.warningSimplified
                  ).toLocaleString()}
            </div>
          </div>
          <div className="info-card">
            <div className="title">
              {t('invoice_view_page.dashboard.rejected')}
            </div>
            <div className="number">
              {loading
                ? '...'
                : (
                    thirdPartyStatusTotals.rejectedVat +
                    thirdPartyStatusTotals.rejectedSimplified
                  ).toLocaleString()}
            </div>
          </div>
        </div>

        <div className="row gap-4 justify-content-between">
          <div className="col chart-container">
            <DashboardChart
              title={t('invoice_view_page.dashboard.sales-e-invoice-status')}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="e-invoice-status"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.accepted_only'),
                  backgroundColor: '#1E2D30',
                  data: chartData.salesAcceptedInvoices,
                },
                {
                  label: t(
                    'invoice_view_page.dashboard.accepted-with-warnings_only',
                  ),
                  backgroundColor: '#547D84',
                  data: chartData.salesAcceptedWithWarnings,
                },
                {
                  label: t('invoice_view_page.dashboard.rejected_only'),
                  backgroundColor: '#C1D0D3',
                  data: chartData.salesRejectedInvoices,
                },
              ]}
            />
          </div>
          <div className="col chart-container">
            <DashboardChart
              title={t(
                'invoice_view_page.dashboard.third-party-e-invoice-status',
              )}
              isTimeseries={true}
              dateFrom={dateFromValue}
              dateTo={dateToValue}
              periodType={getPeriodType()}
              chartType="e-invoice-status"
              dataset={[
                {
                  label: t('invoice_view_page.dashboard.accepted_only'),
                  backgroundColor: '#1E2D30',
                  data: chartData.thirdPartyAcceptedInvoices,
                },
                {
                  label: t(
                    'invoice_view_page.dashboard.accepted-with-warnings_only',
                  ),
                  backgroundColor: '#547D84',
                  data: chartData.thirdPartyAcceptedWithWarnings,
                },
                {
                  label: t('invoice_view_page.dashboard.rejected_only'),
                  backgroundColor: '#C1D0D3',
                  data: chartData.thirdPartyRejectedInvoices,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <Divider />

      <div className="latest-einvoices">
        <div className="d-flex flex-row align-items-center gap-4">
          <h3>{t('invoice_view_page.dashboard.latest-e-invoices')}</h3>
          <div className="d-flex flex-row align-items-center">
            <p className="updated-at">
              {t('invoice_view_page.dashboard.stats_desc', {
                date: moment(invoicesDate)
                  .locale(i18n.language)
                  .format('DD MMM YYYY, h:mm A'),
              })}
            </p>
            <Button
              icon="pi pi-refresh"
              disabled={invoicesLoading}
              link
              onClick={fetchLatestInvoices}
            />
          </div>
        </div>
        <TabView
          activeIndex={activeInvoiceTab}
          onTabChange={(e) => setActiveInvoiceTab(e.index)}
        >
          <TabPanel header={t('invoice_view_page.dashboard.sales')} />
          <TabPanel header={t('invoice_view_page.dashboard.purchases')} />
          <TabPanel header={t('invoice_view_page.dashboard.third-party')} />
        </TabView>

        <DataTable
          rows={10}
          alwaysShowPaginator={false}
          value={latestInvoices}
          loading={invoicesLoading}
          columns={() => [
            {
              label: 'invoice_view_page.dashboard.issue-date',
              value: 'issueDate',
              sortable: false,
              body: (rowData) =>
                rowData.issueDate
                  ? moment.unix(rowData.issueDate).format('DD MMM YYYY')
                  : '-',
            },
            {
              label: 'invoice_view_page.dashboard.issue-time',
              value: 'issueTime',
              sortable: false,
              body: (rowData) =>
                rowData.issueDate
                  ? moment.unix(rowData.issueDate).format('HH:mm:ss')
                  : '-',
            },
            {
              label:
                activeInvoiceTab === 0
                  ? 'invoice_view_page.dashboard.customer-name'
                  : activeInvoiceTab === 1
                  ? 'invoice_view_page.dashboard.supplier-name'
                  : 'invoice_view_page.dashboard.supplier-customer-name',
              value: 'buyerName',
              sortable: false,
            },
            {
              label: 'invoice_view_page.dashboard.currency',
              value: 'currencyCode',
              sortable: false,
            },
            {
              label: 'invoice_view_page.dashboard.net-amount-excluding-vat',
              value: 'netAmount',
              sortable: false,
            },
            {
              label: 'invoice_view_page.dashboard.vat-amount',
              value: 'vatAmount',
              sortable: false,
            },
            {
              label: 'invoice_view_page.dashboard.gross-total-including-vat',
              value: 'grossAmount',
              sortable: false,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default Dashboard;
